<template>
    <b-modal 
        :id="modalId" 
        :title="title" 
        size="lg" 
        scrollable 
        centered 
        ok-only 
        ok-title="Cerrar" 
        ok-variant="primary" 
        class="product-list-modal" 
        body-class="p-0"
    >
        <!-- Header con búsqueda -->
        <div class="modal-custom-header px-4 py-3 bg-light border-bottom">
            <b-input-group>
                <b-form-input 
                    v-model="searchQuery" 
                    placeholder="Buscar productos..." 
                    size="md"
                >
                </b-form-input>
                
            </b-input-group>
        </div>

        <!-- Tabla Principal -->
        <div class="px-4 py-3">
            <b-table 
                :items="filteredProducts" 
                :fields="dynamicFields" 
                :current-page="currentPage" 
                :per-page="pageSize"
                show-empty 
                stacked="md" 
                hover 
                responsive 
                class="modern-table"
            >
                <!-- Columna del Producto -->
                <template #cell(name)="row">
                    <div class="product-info">
                        <div class="product-details">
                            <div class="product-name">{{ row.item.name }}</div>
                            <div class="product-manufacturer">{{ row.item.manufacturer }}</div>
                        </div>
                    </div>
                </template>

                <!-- Estado -->
                <template #cell(state)="row">
                    <div class="state-badge" :class="getStateClass(row.item.state)">
                        <i :class="getStateIcon(row.item.state)"></i>
                        <span class="state-text">{{ row.item.state }}</span>
                    </div>
                </template>

                <!-- Categorías para toxicidad aguda -->
                <template v-if="keyProduct === 'acute_toxicity_mortal'" #cell(categories)="row">
                    <div class="categories-wrapper">
                        <template v-for="(category, type) in row.item.categories">
                            <div v-for="(value, cat) in category" :key="`${type}-${cat}`" v-if="value"
                                class="category-badge" :class="getCategoryClass(cat)">
                                <i class="fa" :class="getCategoryIcon(type)"></i>
                                <span class="category-text">
                                    {{ type === 'oral' ? 'ORAL' : type === 'dermal' ? 'DÉRMICA' : 'INHALACIÓN' }}
                                    {{ cat.split('_')[1] }}
                                </span>
                            </div>
                        </template>
                    </div>
                </template>

                <!-- Grupos de carcinogenicidad -->
                <template v-else-if="keyProduct === 'carcinogenicity'" #cell(categories)="row">
                    <div class="categories-wrapper">
                        <template v-for="(value, group) in row.item.groups">
                            <div v-if="value" :key="group" class="category-badge"
                                :class="getCarcinogenicityClass(group)">
                                <i class="fa fa-exclamation-triangle"></i>
                                <span class="category-text">
                                    {{ getCarcinogenicityLabel(group) }}
                                </span>
                            </div>
                        </template>
                    </div>
                </template>

                <!-- Niveles neurotóxicos -->
                <template v-else-if="keyProduct === 'neurotoxic'" #cell(categories)="row">
                    <div class="categories-wrapper">
                        <div class="category-badge" :class="getNeurotoxicClass(row.item.neurotoxic_level)">
                            <i class="fa fa-brain"></i>
                            <span class="category-text">
                                {{ getNeurotoxicLabel(row.item.neurotoxic_level) }}
                            </span>
                        </div>
                    </div>
                </template>

                <!-- Corrosividad -->
                <template v-else-if="keyProduct === 'corrosive'" #cell(categories)="row">
                    <div class="categories-wrapper">
                        <div v-if="row.item.corrosive.ocular" class="category-badge corrosive-ocular">
                            <i class="fa fa-eye"></i>
                            <span class="category-text">Ojos</span>
                        </div>
                        <div v-if="row.item.corrosive.dermal" class="category-badge corrosive-dermal">
                            <i class="fa fa-hand-paper-o"></i>
                            <span class="category-text">Piel</span>
                        </div>
                    </div>
                </template>

                <!-- Sensibilización -->
                <template v-else-if="keyProduct === 'sensitizing'" #cell(categories)="row">
                    <div class="categories-wrapper">
                        <div v-if="row.item.sensitizing.dermal" class="category-badge sensitizing-dermal">
                            <i class="fa fa-hand-paper"></i>
                            <span class="category-text">Cutánea</span>
                        </div>
                        <div v-if="row.item.sensitizing.respiratory" class="category-badge sensitizing-respiratory">
                            <i class="fa fa-lungs"></i>
                            <span class="category-text">Respiratorios</span>
                        </div>
                    </div>
                </template>

                <!-- Toxicidad Aguda -->
                <template v-else-if="keyProduct === 'toxicity_agude_toxicity'" #cell(categories)="row">
                    <div class="categories-wrapper">
                        <div v-if="row.item.toxicities.product_toxicity_agude_ingestion" 
                             class="category-badge toxicity-ingestion">
                            <i class="fa fa-medkit"></i>
                            <span class="category-text">Ingestión</span>
                        </div>
                        <div v-if="row.item.toxicities.product_toxicity_inhalation" 
                             class="category-badge toxicity-inhalation">
                            <i class="fa fa-cloud"></i>
                            <span class="category-text">Inhalación</span>
                        </div>
                        <div v-if="row.item.toxicities.product_toxicity_dermal" 
                             class="category-badge toxicity-dermal">
                            <i class="fa fa-hand-paper-o"></i>
                            <span class="category-text">Dérmica</span>
                        </div>
                    </div>
                </template>

                <!-- Órganos Diana -->
                <template v-else-if="keyProduct === 'target_organs'" #cell(categories)="row">
                    <div class="categories-wrapper">
                        <div v-if="row.item.target_organs.product_target_organs_exposition_repetitive" 
                             class="category-badge target-organs-repetitive">
                            <i class="fa fa-refresh"></i>
                            <span class="category-text">Exposición repetida</span>
                        </div>
                        <div v-if="row.item.target_organs.product_target_organs_exposition_unique" 
                             class="category-badge target-organs-unique">
                            <i class="fa fa-exclamation-circle"></i>
                            <span class="category-text">Exposición única</span>
                        </div>
                    </div>
                </template>

                <!-- Ubicaciones -->
                <template #cell(locations)="row">
                    <b-button 
                        v-if="row.item.locations.length" 
                        variant="outline-primary" 
                        size="sm"
                        @click="row.toggleDetails" 
                        class="location-btn"
                    >
                        <i class="fa" :class="row.detailsShowing ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                        {{ row.detailsShowing ? 'Ocultar' : 'Ver' }}
                    </b-button>
                    <span v-else class="text-muted">
                        <i class="fa fa-map-marker-slash"></i> Sin ubicaciones
                    </span>
                </template>

                <!-- Detalles de ubicaciones -->
                <template #row-details="row">
                    <div class="location-details">
                        <div v-for="(location, index) in row.item.locations" :key="index" class="location-item">
                            <i class="fa fa-map-marker-alt location-icon"></i>
                            <div class="location-path" translate="no">
                                {{ formatLocationPath(location) }}
                            </div>
                        </div>
                    </div>
                </template>
            </b-table>

            <!-- Estado vacío -->
            <div v-if="filteredProducts.length === 0" class="empty-state">
                <i class="fa fa-box-open empty-icon"></i>
                <p class="empty-text">No hay productos disponibles para mostrar.</p>
            </div>
        </div>

        <!-- Paginación -->
        <template #modal-footer>
            <div class="w-100 d-flex justify-content-between align-items-center">
                <div class="per-page-selector">
                    <b-form-select 
                        v-model="pageSize" 
                        :options="pageSizeOptions" 
                        size="sm" 
                        class="w-auto"
                    >
                    </b-form-select>
                </div>
                <b-pagination 
                    v-model="currentPage" 
                    pills 
                    :total-rows="total" 
                    :per-page="pageSize" 
                    align="center"
                    class="mb-0 custom-pagination"
                >
                </b-pagination>
            </div>
        </template>
    </b-modal>
</template>

<script>
export default {
    name: 'ProductListModal',
    props: {
        modalId: {
            type: String,
            required: true
        },
        title: {
            type: String,
            required: true
        },
        products: {
            type: Array,
            default: () => []
        },
        keyProduct: {
            type: String,
            default: () => null
        },
    },
    data() {
        return {
            currentPage: 1,
            pageSize: 5,
            searchQuery: '',
            pageSizeOptions: [
                { value: 5, text: '5 por página' },
                { value: 10, text: '10 por página' },
                { value: 20, text: '20 por página' },
                { value: 30, text: '30 por página' },
                { value: 100, text: '100 por página' }
            ],
            carcinogenicityGroups: {
                group_1: {
                    name: 'Carcinógeno para seres humanos',
                    color: '#068190'
                },
                group_2A: {
                    name: 'Probablemente carcinógeno',
                    color: '#04373D'
                },
                group_2B: {
                    name: 'Posiblemente carcinógeno',
                    color: '#117ACB'
                },
                reported_by_sds: {
                    name: 'Reportado FDB',
                    color: '#0FBEF9'
                }
            },
            neurotoxicLevels: {
                level_1: {
                    name: 'Nivel 1',
                    color: '#068190'
                },
                level_2: {
                    name: 'Nivel 2',
                    color: '#04373D'
                },
                level_3: {
                    name: 'Nivel 3',
                    color: '#674485'
                },
                level_4: {
                    name: 'Nivel 4',
                    color: '#117ACB'
                },
                level_for_sga: {
                    name: 'Otros',
                    color: '#B45DFF'
                }
            },
            toxicityAgudeToxicityCategories: {
                toxicity_agude_ingestion: {
                    name: 'Ingestión',
                    color: '#04373D'
                },
                toxicity_dermal: {
                    name: 'Dérmica',
                    color: '#117ACB'
                },
                toxicity_inhalation: {
                    name: 'Inhalación',
                    color: '#0FBEF9'
                }
            },
            targetOrgansCategories: {
                target_organs_exposition_repetitive: {
                    name: 'Exposición repetida',
                    color: '#117ACB'
                },
                target_organs_exposition_unique: {
                    name: 'Exposición única',
                    color: '#0FBEF9'
                }
            },
            baseFields: [
                {
                    key: 'name',
                    label: 'Producto',
                    sortable: true,
                    thClass: 'th-product',
                    tdClass: 'td-product'
                },
                {
                    key: 'state',
                    label: 'Estado',
                    thClass: 'text-center th-state',
                    tdClass: 'text-center td-state'
                },
                {
                    key: 'locations',
                    label: 'Ubicaciones',
                    thClass: 'text-center th-locations',
                    tdClass: 'text-center td-locations'
                }
            ]
        }
    },
    computed: {
        dynamicFields() {
            const fields = [...this.baseFields];
            if (this.keyProduct === 'acute_toxicity_mortal' ||
                this.keyProduct === 'carcinogenicity' ||
                this.keyProduct === 'neurotoxic' ||
                this.keyProduct === 'corrosive' ||
                this.keyProduct === 'sensitizing' ||
                this.keyProduct === 'toxicity_agude_toxicity' ||
                this.keyProduct === 'target_organs') {
                fields.splice(2, 0, {
                    key: 'categories',
                    label: this.getCategoryLabel(),
                    thClass: 'text-center th-categories',
                    tdClass: 'text-center td-categories'
                });
            }
            return fields;
        },
        filteredProducts() {
            if (!this.searchQuery) return this.products;
            const query = this.searchQuery.toLowerCase();
            return this.products.filter(product =>
                product.name.toLowerCase().includes(query) ||
                product.manufacturer.toLowerCase().includes(query)
            );
        },
        total() {
            return this.filteredProducts.length;
        }
    },
    methods: {
        getCategoryLabel() {
            const labels = {
                'acute_toxicity_mortal': 'Categorías',
                'carcinogenicity': 'Grupos',
                'neurotoxic': 'Nivel Neurotóxico',
                'corrosive': 'Corrosividad',
                'sensitizing': 'Sensibilizantes',
                'toxicity_agude_toxicity': 'Toxicidad',
                'target_organs': 'Exposición'
            };
            return labels[this.keyProduct] || 'Categorías';
        },
        getStateClass(state) {
            const stateMap = {
                'Liquido': 'state-liquid',
                'Solido': 'state-solid',
                'Gaseoso': 'state-gas'
            };
            return stateMap[state] || '';
        },
        getStateIcon(state) {
            const iconMap = {
                'Liquido': 'fa fa-tint',
                'Solido': 'fa fa-cube',
                'Gaseoso': 'fa fa-wind'
            };
            return iconMap[state] || 'fa fa-question';
        },
        getCategoryClass(category) {
            return category === 'category_1' ? 'category-primary' : 'category-success';
        },
        getCategoryIcon(type) {
            const iconMap = {
                'oral': 'fa-pills',
                'dermal': 'fa-hand-paper',
                'inhalation': 'fa-lungs'
            };
            return iconMap[type] || 'fa-exclamation-triangle';
        },
        formatLocationPath(location) {
            const path = [];
            let current = location;
            while (current) {
                path.unshift(current.description);
                current = current.parent_location;
            }
            return path.join(' > ');
        },
        getCarcinogenicityClass(group) {
            return `carcinogenicity-${group.toLowerCase()}`;
        },
        getCarcinogenicityLabel(group) {
            return this.carcinogenicityGroups[group] && this.carcinogenicityGroups[group].name || group;
        },
        getNeurotoxicClass(level) {
            return `neurotoxic-${level.toLowerCase()}`;
        },
        getNeurotoxicLabel(level) {
            return this.neurotoxicLevels[level] && this.neurotoxicLevels[level].name || level;
        }
    }
}
</script>

<style scoped>
.product-list-modal ::v-deep .modal-content {
    border-radius: 1rem;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.modal-custom-header {
    background: linear-gradient(to right, #f8f9fa, #ffffff);
}

/* Tabla Moderna */
.modern-table {
    --bs-table-hover-bg: rgba(0, 123, 255, 0.04);
}

.modern-table ::v-deep th {
    background: #f8f9fa;
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.8rem;
    letter-spacing: 0.5px;
    padding: 1rem;
}

/* Producto */
.product-info {
    display: flex;
    align-items: center;
}

.product-details {
    flex: 1;
}

.product-name {
    font-weight: 600;
    color: #2d3748;
    margin-bottom: 0.25rem;
}

.product-manufacturer {
    font-size: 0.875rem;
    color: #718096;
}

/* Estados */
.state-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    padding: 0.2rem 0.5rem;
    border-radius: 0.25rem;
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1;
    text-transform: uppercase;
    letter-spacing: 0.02em;
}

.state-badge i {
    font-size: 0.75rem;
}

.state-text {
    position: relative;
    top: 0.5px;
}

.state-liquid {
    background-color: #e3f2fd;
    color: #1976d2;
}

.state-solid {
    background-color: #f3e5f5;
    color: #7b1fa2;
}

.state-gas {
    background-color: #e8f5e9;
    color: #388e3c;
}

/* Categorías */
.categories-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    justify-content: center;
}

.category-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    padding: 0.2rem 0.5rem;
    border-radius: 0.25rem;
    font-size: 0.688rem;
    font-weight: 600;
    line-height: 1;
    letter-spacing: 0.02em;
    white-space: nowrap;
}

.category-badge i {
    font-size: 0.688rem;
}

.category-text {
    position: relative;
    top: 0.5px;
}

.category-primary {
    background-color: rgba(12, 177, 247, 0.1);
    color: #0CB1F7;
}

.category-success {
    background-color: rgba(4, 55, 61, 0.1);
    color: #04373D;
}

/* Ubicaciones */
.location-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
}

.location-details {
    background: #f8fafc;
    margin: 1rem;
    border-radius: 0.5rem;
    padding: 1rem;
}

.location-item {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.75rem;
    border-bottom: 1px solid #e2e8f0;
}

.location-item:last-child {
    border-bottom: none;
}

.location-icon {
    color: #64748b;
    margin-top: 0.25rem;
}

.location-path {
    flex: 1;
    font-size: 0.9rem;
    color: #475569;
    line-height: 1.5;
}

/* Estado vacío */
.empty-state {
    text-align: center;
    padding: 3rem 1rem;
}

.empty-icon {
    font-size: 3rem;
    color: #cbd5e0;
    margin-bottom: 1rem;
}

.empty-text {
    color: #718096;
    font-size: 1rem;
    margin: 0;
}

/* Paginación */
::v-deep .page-item.active .page-link {
    background-color: #4a6bff;
    border-color: #4a6bff;
}

::v-deep .page-link {
    color: #4a6bff;
}

.per-page-selector {
    width: auto;
}

::v-deep .custom-select {
    border-radius: 0.5rem;
    border-color: #e2e8f0;
    padding: 0.375rem 2rem 0.375rem 0.75rem;
}

/* Custom styles for purple pagination */
.custom-pagination ::v-deep .page-item.active .page-link {
    background-color: #0CB1F7 !important;
    border-color: #0CB1F7 !important;
    color: white !important;
}

.custom-pagination ::v-deep .page-link {
    color: #0CB1F7 !important;
}

.custom-pagination ::v-deep .page-link:hover {
    background-color: #e9ecef;
    border-color: #dee2e6;
}

.custom-pagination ::v-deep .page-item.disabled .page-link {
    color: #6c757d !important;
}

/* Estilos específicos para grupos de carcinogenicidad */
.carcinogenicity-group_1 {
    background-color: rgba(6, 129, 144, 0.1);
    color: #068190;
}

.carcinogenicity-group_2a {
    background-color: rgba(4, 55, 61, 0.1);
    color: #04373D;
}

.carcinogenicity-group_2b {
    background-color: rgba(17, 122, 203, 0.1);
    color: #117ACB;
}

.carcinogenicity-reported_by_sds {
    background-color: rgba(15, 190, 249, 0.1);
    color: #0FBEF9;
}

/* Estilos específicos para niveles neurotóxicos */
.neurotoxic-level_1 {
    background-color: rgba(6, 129, 144, 0.1);
    color: #068190;
}

.neurotoxic-level_2 {
    background-color: rgba(4, 55, 61, 0.1);
    color: #04373D;
}

.neurotoxic-level_3 {
    background-color: rgba(103, 68, 133, 0.1);
    color: #674485;
}

.neurotoxic-level_4 {
    background-color: rgba(17, 122, 203, 0.1);
    color: #117ACB;
}

.neurotoxic-level_for_sga {
    background-color: rgba(180, 93, 255, 0.1);
    color: #B45DFF;
}

/* Estilos específicos para corrosividad */
.corrosive-ocular {
    background-color: rgba(4, 55, 61, 0.1);
    color: #04373D;
}

.corrosive-dermal {
    background-color: rgba(15, 190, 249, 0.1);
    color: #0FBEF9;
}

/* Estilos específicos para sensibilización */
.sensitizing-dermal {
    background-color: rgba(6, 129, 144, 0.1);
    color: #068190;
}

.sensitizing-respiratory {
    background-color: rgba(4, 55, 61, 0.1);
    color: #04373D;
}

/* Estilos para toxicidad aguda */
.toxicity-ingestion {
    background-color: rgba(6, 129, 144, 0.1);
    color: #068190;
}

.toxicity-inhalation {
    background-color: rgba(17, 122, 203, 0.1);
    color: #117ACB;
}

.toxicity-dermal {
    background-color: rgba(15, 190, 249, 0.1);
    color: #0FBEF9;
}

/* Estilos para órganos diana */
.target-organs-repetitive {
    background-color: rgba(103, 68, 133, 0.1);
    color: #674485;
}

.target-organs-unique {
    background-color: rgba(180, 93, 255, 0.1);
    color: #B45DFF;
}
</style>
